import React from "react";
import { GenericTable, GenericTableColumn, type GenericTableProps } from "@/styles/GenericTable";
import { GenericTag } from "@/styles/GenericTag";
import { PurchaseOrder } from "../types";

interface PurchaseOrdersTableProps {
  /** The server-driven list state (`usePurchaseOrders().tableProps`): rows, loading, sort, paging. */
  tableProps: Pick<GenericTableProps<PurchaseOrder>, 'data' | 'loading' | 'server' | 'pageSize' | 'page' | 'onPageChange' | 'sort' | 'onSortChange'>;
  onOpen: (item: PurchaseOrder) => void;
  projectIdToName?: Map<string, string>;
  vendorIdToName?: Map<string, string>;
  selectedRows: string[];
  onSelectedRowsChange: (selected: string[]) => void;
}

export function PurchaseOrdersTable({ tableProps, onOpen, projectIdToName, vendorIdToName, selectedRows, onSelectedRowsChange }: PurchaseOrdersTableProps) {
  function formatDMY(dateStr?: string) {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  }

  // Map purchase order statuses to GenericTag status types
  const mapPOStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'voided' | 'completed' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'sent':
        return 'sent';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      case 'voided':
        return 'voided';
      case 'completed':
        return 'completed';
      default:
        return 'draft';
    }
  };

  // Format status text with proper capitalization
  const formatPOStatusText = (status: string): string => {
    return status.replace('_', ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase());
  };

  // Column definitions for GenericTable
  const columns: GenericTableColumn<PurchaseOrder>[] = [
    {
      key: 'poNumber',
      header: 'PO ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'po_number',
      render: (row) => <span className="font-medium">{row.poNumber}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12rem',
      sortable: true,
      sortKey: 'title',
      render: (row) => row.title,
    },
    {
      key: 'projectId',
      header: 'Project',
      width: '10rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => projectIdToName?.get(row.projectId) ?? row.projectId,
    },
    {
      key: 'vendorId',
      header: 'Vendor',
      width: '10rem',
      sortable: true,
      sortKey: 'vendor',
      render: (row) => row.vendorId ? (vendorIdToName?.get(row.vendorId) ?? row.vendorId) : '',
    },
    {
      key: 'status',
      header: 'Status',
      width: '7rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapPOStatusToTag(row.status)}
        >
          {formatPOStatusText(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '7rem',
      align: 'right' as const,
      sortable: true,
      sortKey: 'amount',
      render: (row) => {
        if (row.amount == null) return '';
        const amount = Number(row.amount);
        return isNaN(amount) ? '' : `$${amount.toLocaleString()}`;
      },
    },
    {
      key: 'receivedAt',
      header: 'Completion Date',
      width: '9rem',
      sortable: true,
      sortKey: 'complete_by',
      render: (row) => formatDMY(row.receivedAt),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.5rem',
      align: 'center' as const,
      render: (row) => row.attachments.length,
    },
    {
      key: 'commentsCount',
      header: 'Comments',
      width: '6.5rem',
      align: 'center' as const,
      render: (row) => row.commentsCount ?? 0,
    },
  ];

  return (
    <GenericTable
      {...tableProps}
      columns={columns}
      rowKey={(row) => row.id}
      scrollContainerMaxWidth="calc(100vw - 19.2rem)"
      minTableWidth={1200}
      onRowClick={onOpen}
      selectable={true}
      selectedKeys={selectedRows}
      onSelectionChange={onSelectedRowsChange}
      emptyState={!tableProps.server?.hasMore ? 'No purchase orders yet' : undefined}
    />
  );
}
